import { ProphecyLoader } from '@common/ProphecyLoader';
import { isSignupEntryPath } from '@common/url';
import { useHubSpot, useMetaPixel, useOAuthSignupTracking } from '@pages/auth/analyticsHooks';
import { useOnboardingWarnings } from '@pages/auth/useOnboardingWarnings';
import { CacheManagerProvider } from '@prophecy/utils/react/cache/context';
import { useOnce } from '@prophecy/utils/react/hooks';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { lazy, Suspense, useEffect, useRef } from 'react';

import { useAppMetadata } from './context/appMetadata';
import { useMixPanel } from './context/mixpanel/context';
import { MixPanelContextKeys } from './context/mixpanel/types';
import { UserProvider } from './context/user';
import { isReactQueryDevtoolsEnabled } from './utils/localstorage-keys';

// Exported import factories so the bootstrap (index.tsx) can pre-warm the bundle for the
// resolved auth state. Both these and the lazy() below call the same import(), so they share
// one entry in the module map — pre-warming never causes a second fetch.
export const prefetchPrivateRoutes = () => import(/* vitePrefetch: true */ './router/PrivateRoutes');
export const prefetchPublicRoutes = () => import(/* vitePrefetch: true */ './router/PublicRoutes');

const PrivateRoutes = lazy(prefetchPrivateRoutes);
const PublicRoutes = lazy(prefetchPublicRoutes);

export const MainApp = ({ isLoggedIn, userId }: { isLoggedIn: boolean; userId: string }) => {
  const { addContext, removeContext, loadFullStory, publicInitMixpanel, privateMixpanelInit } = useMixPanel();
  const { user } = useAppMetadata();

  const isMixpanelLoaded = useRef(false);
  const showLoggedInState = isLoggedIn && !isSignupEntryPath(window.location.pathname);

  useHubSpot();
  useMetaPixel();
  useOAuthSignupTracking(user);
  // Surface (and flush) any onboarding failures persisted for this user during signup/invite.
  useOnboardingWarnings(showLoggedInState ? user : undefined);

  useEffect(() => {
    if (isLoggedIn && user?.id)
      addContext(MixPanelContextKeys.User, {
        email: user.email,
        id: user.id,
        type: user.type
      });
    else {
      removeContext(MixPanelContextKeys.User);
    }
  }, [isLoggedIn, user, addContext, removeContext]);

  useEffect(() => {
    if (isMixpanelLoaded.current) return;

    setTimeout(async () => {
      loadFullStory();
      if (showLoggedInState) {
        await privateMixpanelInit();
      } else {
        await publicInitMixpanel();
      }
      isMixpanelLoaded.current = true;
    }, 0);
  }, [showLoggedInState, isMixpanelLoaded, loadFullStory, privateMixpanelInit, publicInitMixpanel]);

  const queryClient = useOnce(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            networkMode: 'offlineFirst'
          },
          mutations: {
            networkMode: 'offlineFirst'
          }
        }
      })
  );

  return isLoggedIn ? (
    <QueryClientProvider client={queryClient}>
      {/* The SQL IDE mounts its own per historical view; shared gem-dialog atoms need one on every other IDE too. */}
      <CacheManagerProvider>
        <UserProvider userUID={userId as string}>
          <Suspense fallback={<ProphecyLoader />}>
            <PrivateRoutes />
          </Suspense>
        </UserProvider>
      </CacheManagerProvider>
      {isReactQueryDevtoolsEnabled && <ReactQueryDevtools></ReactQueryDevtools>}
    </QueryClientProvider>
  ) : (
    <Suspense fallback={<ProphecyLoader />}>
      <PublicRoutes />
    </Suspense>
  );
};
